import type { Proposal, ProposalItemInput } from '@pm/schemas';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
} from '@pm/ui';
import { Check, ChevronRight, FileSignature, Plus, Send, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtMoney } from '@/lib/format';
import { comKeys, useInvalidating, useProposal, useServices } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { ProposalBadge } from './commercial-bits';

type Draft = ProposalItemInput & {
  key: string;
  description: string;
  unit_price: string;
  quantity: string;
};

/** SPEC §9 — editor de proposta: itens com snapshot, envio, aprovação (→ contrato) e rejeição. */
export function ProposalPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const { data: p, isLoading, isError, error, refetch } = useProposal(id);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const inval = [comKeys.proposal(id ?? ''), ['proposals'], ['contracts']];
  const send = useInvalidating(() => api.post<Proposal>(`/proposals/${id}/send`), inval);
  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !p) return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const editable = p.status === 'DRAFT' && can('proposals.create');
  const decidable = (p.status === 'DRAFT' || p.status === 'SENT') && can('proposals.approve');

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/comercial?tab=proposals" className="hover:text-text-1">
              Propostas
            </Link>
            <ChevronRight className="size-3" />
            <span>#{p.number}</span>
          </div>
        }
        title={`Proposta #${p.number} · ${p.client.name}`}
        actions={
          <>
            <ProposalBadge status={p.status} />
            {editable ? (
              <Button
                variant="secondary"
                loading={send.isPending}
                disabled={p.items.length === 0}
                onClick={() =>
                  send.mutate(undefined, {
                    onSuccess: () => toast.success('Marcada como enviada'),
                    onError: (e) => toast.error(e),
                  })
                }
              >
                <Send /> Marcar enviada
              </Button>
            ) : null}
            {decidable ? (
              <Button variant="ghost" onClick={() => setRejectOpen(true)}>
                <X /> Rejeitar
              </Button>
            ) : null}
            {decidable ? (
              <Button
                variant="primary"
                disabled={p.items.length === 0}
                onClick={() => setApproveOpen(true)}
              >
                <Check /> Aprovar
              </Button>
            ) : null}
            {p.contract_id ? (
              <Link
                to={`/comercial/contratos/${p.contract_id}`}
                className="inline-flex h-control items-center gap-1.5 rounded border border-border px-3 text-sm hover:bg-surface-2"
              >
                <FileSignature className="size-4" /> Ver contrato
              </Link>
            ) : null}
          </>
        }
      />
      <div className="grid gap-gap-lg lg:grid-cols-[1fr_300px]">
        {editable ? <ItemsEditor proposal={p} /> : <ItemsTable proposal={p} />}
        <aside className="flex flex-col gap-4 self-start rounded-md border border-border bg-surface-1 p-4">
          <div>
            <LabelCaps>Total</LabelCaps>
            <p className="font-mono text-2xl">{fmtMoney(p.total)}</p>
          </div>
          <MetaEditor proposal={p} editable={editable} />
          <div className="flex flex-col gap-1 font-mono text-xs text-text-3">
            <span>
              Criada {fmtDateTime(p.created_at)}
              {p.created_by ? ` · ${p.created_by.name}` : ''}
            </span>
            {p.sent_at ? <span>Enviada {fmtDateTime(p.sent_at)}</span> : null}
            {p.decided_at ? (
              <span>
                {p.status === 'APPROVED' ? 'Aprovada' : 'Decidida'} {fmtDateTime(p.decided_at)}
                {p.decided_by ? ` · ${p.decided_by.name}` : ''}
              </span>
            ) : null}
          </div>
        </aside>
      </div>
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent
          title="Aprovar proposta"
          description="Os itens viram serviços do contrato com os valores desta proposta."
          size="sm"
        >
          <ApproveForm
            proposalId={p.id}
            onDone={(contractId) => {
              setApproveOpen(false);
              if (contractId) void navigate(`/comercial/contratos/${contractId}`);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent title="Rejeitar proposta" size="sm">
          <RejectForm proposalId={p.id} onDone={() => setRejectOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemsTable({ proposal }: { proposal: Proposal }) {
  return (
    <div className="rounded-md border border-border bg-surface-1">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
            <th className="h-8 px-3 text-left font-medium">Item</th>
            <th className="h-8 px-3 text-right font-medium">Qtd</th>
            <th className="h-8 px-3 text-right font-medium">Unitário</th>
            <th className="h-8 px-3 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {proposal.items.map((it) => (
            <tr key={it.id} className="h-row border-b border-border last:border-b-0">
              <td className="px-3 text-sm">
                {it.description}
                {it.service ? (
                  <span className="ml-2 font-mono text-[10px] text-text-3">catálogo</span>
                ) : null}
              </td>
              <td className="px-3 text-right font-mono text-sm">{it.quantity}</td>
              <td className="px-3 text-right font-mono text-sm">{fmtMoney(it.unit_price)}</td>
              <td className="px-3 text-right font-mono text-sm">{fmtMoney(it.total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {proposal.items.length === 0 ? (
        <p className="p-6 text-center text-sm text-text-3">Sem itens.</p>
      ) : null}
    </div>
  );
}

function ItemsEditor({ proposal }: { proposal: Proposal }) {
  const toast = useToast();
  const { data: services = [] } = useServices();
  const [items, setItems] = useState<Draft[]>(() =>
    proposal.items.map((it) => ({
      key: it.id,
      service_id: it.service?.id ?? null,
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
    })),
  );
  const [dirty, setDirty] = useState(false);
  const save = useInvalidating(
    (list: ProposalItemInput[]) =>
      api.patch<Proposal>(`/proposals/${proposal.id}`, { items: list }),
    [comKeys.proposal(proposal.id), ['proposals']],
  );
  const upd = (key: string, patch: Partial<Draft>) => {
    setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    setDirty(true);
  };
  const add = () => {
    setItems((xs) => [
      ...xs,
      {
        key: crypto.randomUUID(),
        service_id: null,
        description: '',
        quantity: '1',
        unit_price: '',
      },
    ]);
    setDirty(true);
  };
  const pickService = (key: string, sid: string) => {
    const s = services.find((x) => x.id === sid);
    upd(key, {
      service_id: sid || null,
      description: s?.name ?? '',
      unit_price: s?.base_price ?? '',
    });
  };
  const total = items.reduce(
    (n, it) => n + Number(it.quantity || 0) * Number(it.unit_price || 0),
    0,
  );
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-1 p-3">
      <div className="grid grid-cols-[1fr_1fr_80px_120px_32px] gap-2 px-1 font-mono text-[10px] uppercase tracking-[var(--tracking-caps)] text-text-3">
        <span>Serviço</span>
        <span>Descrição</span>
        <span className="text-right">Qtd</span>
        <span className="text-right">Unitário</span>
        <span />
      </div>
      {items.map((it) => (
        <div key={it.key} className="grid grid-cols-[1fr_1fr_80px_120px_32px] items-center gap-2">
          <Select
            value={it.service_id ?? ''}
            onChange={(e) => pickService(it.key, e.target.value)}
            className="h-control-sm text-sm"
          >
            <option value="">Avulso</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
          <Input
            value={it.description}
            onChange={(e) => upd(it.key, { description: e.target.value })}
            placeholder="Descrição"
            className="h-control-sm text-sm"
          />
          <Input
            type="number"
            min={0}
            step="0.001"
            mono
            value={it.quantity}
            onChange={(e) => upd(it.key, { quantity: e.target.value })}
            className="h-control-sm text-right text-sm"
          />
          <Input
            type="number"
            min={0}
            step="0.01"
            mono
            value={it.unit_price}
            onChange={(e) => upd(it.key, { unit_price: e.target.value })}
            className="h-control-sm text-right text-sm"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Remover"
            onClick={() => {
              setItems((xs) => xs.filter((x) => x.key !== it.key));
              setDirty(true);
            }}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={add}>
          <Plus /> Item
        </Button>
        <span className="ml-auto font-mono text-sm text-text-2">Total: {fmtMoney(total)}</span>
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || items.some((i) => !i.description.trim())}
          loading={save.isPending}
          onClick={() =>
            save.mutate(
              items.map((i) => ({
                service_id: i.service_id ?? null,
                description: i.description,
                quantity: i.quantity || '1',
                unit_price: i.unit_price === '' ? undefined : Number(i.unit_price).toFixed(2),
              })),
              {
                onSuccess: (p) => (
                  toast.success('Itens salvos'),
                  setDirty(false),
                  setItems(
                    p.items.map((x) => ({
                      key: x.id,
                      service_id: x.service?.id ?? null,
                      description: x.description,
                      quantity: x.quantity,
                      unit_price: x.unit_price,
                    })),
                  )
                ),
                onError: (e) => toast.error(e),
              },
            )
          }
        >
          Salvar itens
        </Button>
      </div>
    </div>
  );
}

function MetaEditor({ proposal, editable }: { proposal: Proposal; editable: boolean }) {
  const toast = useToast();
  const [valid, setValid] = useState(proposal.valid_until ?? '');
  const [notes, setNotes] = useState(proposal.notes ?? '');
  const save = useInvalidating(
    (input: Record<string, unknown>) => api.patch<Proposal>(`/proposals/${proposal.id}`, input),
    [comKeys.proposal(proposal.id), ['proposals']],
  );
  if (!editable)
    return (
      <div className="flex flex-col gap-2 text-sm">
        <div>
          <LabelCaps>Validade</LabelCaps>
          <p className="font-mono">
            {proposal.valid_until ? fmtDate(`${proposal.valid_until}T12:00:00`) : '—'}
          </p>
        </div>
        <div>
          <LabelCaps>Observações</LabelCaps>
          <p className="whitespace-pre-wrap text-text-2">{proposal.notes ?? '—'}</p>
        </div>
      </div>
    );
  return (
    <div className="flex flex-col gap-3">
      <Field label="Validade" htmlFor="pp-valid">
        <Input
          id="pp-valid"
          type="date"
          mono
          value={valid}
          onChange={(e) => setValid(e.target.value)}
          onBlur={() =>
            (valid || null) !== proposal.valid_until &&
            save.mutate({ valid_until: valid || null }, { onError: (e) => toast.error(e) })
          }
          className="h-control-sm text-sm"
        />
      </Field>
      <Field label="Observações" htmlFor="pp-notes">
        <Textarea
          id="pp-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() =>
            (notes || null) !== proposal.notes &&
            save.mutate({ notes: notes || null }, { onError: (e) => toast.error(e) })
          }
          className="min-h-[80px] text-sm"
        />
      </Field>
    </div>
  );
}

function ApproveForm({
  proposalId,
  onDone,
}: {
  proposalId: string;
  onDone: (contractId: string | null) => void;
}) {
  const toast = useToast();
  const [createContract, setCreateContract] = useState(true);
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState('');
  const approve = useInvalidating(
    (input: Record<string, unknown>) =>
      api.post<{ proposal: Proposal; contract: { id: string } | null }>(
        `/proposals/${proposalId}/approve`,
        input,
      ),
    [comKeys.proposal(proposalId), ['proposals'], ['contracts']],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        approve.mutate(
          { create_contract: createContract, start_date: start, end_date: end || null },
          {
            onSuccess: (r) => (
              toast.success(
                r.contract ? 'Proposta aprovada e contrato criado' : 'Proposta aprovada',
              ),
              onDone(r.contract?.id ?? null)
            ),
            onError: (err) => toast.error(err),
          },
        );
      }}
    >
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={createContract} onCheckedChange={(v) => setCreateContract(v === true)} />{' '}
        Criar contrato a partir desta proposta
      </label>
      {createContract ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Início" htmlFor="ap-start">
            <Input
              id="ap-start"
              type="date"
              mono
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </Field>
          <Field label="Fim (opcional)" htmlFor="ap-end">
            <Input
              id="ap-end"
              type="date"
              mono
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </Field>
        </div>
      ) : null}
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={() => onDone(null)}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={approve.isPending}>
          <Check /> Aprovar
        </Button>
      </DialogFooter>
    </form>
  );
}

function RejectForm({ proposalId, onDone }: { proposalId: string; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const reject = useInvalidating(
    (r: string) => api.post<Proposal>(`/proposals/${proposalId}/reject`, { reason: r }),
    [comKeys.proposal(proposalId), ['proposals']],
  );
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        reject.mutate(reason, {
          onSuccess: () => (toast.success('Proposta rejeitada'), onDone()),
          onError: (err) => toast.error(err),
        });
      }}
    >
      <Field label="Motivo" htmlFor="rj-reason">
        <Textarea
          id="rj-reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="min-h-[80px]"
        />
      </Field>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          type="submit"
          loading={reject.isPending}
          disabled={reason.trim().length < 2}
        >
          Rejeitar
        </Button>
      </DialogFooter>
    </form>
  );
}
